import { Component, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Validators, UntypedFormControl, FormGroup, UntypedFormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import * as register from '../../actions/register.actions';
import * as login from '../../actions/login.actions';
import * as logout from '../../actions/logout.actions';
import * as forgotPasscodeUserNeedsHelpActions from '../../actions/forgot-passcode.actions';
import { IRegistrationLoginRequest, IRegistrationLoginSuccess } from '../../models/registration-login.model';
import * as registerSelector from '../../selectors/register.selectors';
import * as loginSelector from '../../selectors/login.selectors';
import * as forgotPasscodeSelector from '../../selectors/forgot-passcode.selectors';
import * as checkIfRegisteredSelector from '../../selectors/check-if-registered.selectors';
import { Observable, Subscription } from 'rxjs';
import { ErrorResponse, IGenericError } from '../../../../core/models/generic-models';
import { IForgotPasscodeRequest } from '../../models/forgot-passcode.model';
import { ICheckRegisterRequiredRequest, ICheckRegisterRequiredResponse } from 'src/app/core/models/check-registration.model';
import { map } from 'rxjs/operators';
import { ISignupSetPasscode } from '../../models/signup-set-passcode.model';
import { ISignupRequest, ISignupResponse } from '../../models/signup.model';
import { fromSignupSetPasscodeActions } from '../../actions';
import { fromSignUpSelectors } from '../../selectors';

@Component({
  selector: 'app-passcode',
  templateUrl: './passcode.component.html',
  styleUrls: ['./passcode.component.css']
})
export class PasscodeComponent implements OnInit, OnDestroy {
  browserRefresh: boolean;
  isRegistrationLoading$: Observable<boolean>;
  registrationFailure$: Observable<IGenericError<ErrorResponse>>;
  isForgotPasswordLoading$: Observable<boolean>;
  forgotPasswordFailure$: Observable<IGenericError<ErrorResponse>>;
  getCheckIfRegistered$: Observable<ICheckRegisterRequiredResponse>;
  getPasscodeRegisterSuccess$: Observable<IRegistrationLoginSuccess>;
  getPasscodeLoginSuccess$: Observable<IRegistrationLoginSuccess>;
  getUserData$: Observable<ICheckRegisterRequiredRequest>;
  getSignUpResponse$: Observable<ISignupResponse>;
  getSignUpRequest$: Observable<ISignupRequest>;
  getUserDataSubscription: Subscription = new Subscription();
  subscription: Subscription;
  isThereData = undefined;
  userMobileNumber: string;
  isWrongPasscode: boolean;
  jwtToken: string;

  passwordForm = this.fb.group({
    passcode: new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9]+$'), Validators.minLength(4)])
  });

  constructor(
    private router: Router,
    private fb: UntypedFormBuilder,
    private store: Store
  ) {
    this.isWrongPasscode = false;
    this.getCheckIfRegistered$ = this.store.select(checkIfRegisteredSelector.getCheckRegistrationSuccess);
    this.getUserData$ = this.store.select(checkIfRegisteredSelector.getCheckRegistrationRequest);
    this.getPasscodeRegisterSuccess$ = this.store.select(registerSelector.getRegistrationSuccess);
    this.getPasscodeLoginSuccess$ = this.store.select(loginSelector.getLoginSuccess);
    this.getSignUpResponse$ = this.store.select(fromSignUpSelectors.getSignupSuccess);
  }

  ngOnInit(): void {
    this.getUserDataSubscription.add(
      this.getUserData$.subscribe(userDetails => {
        if (userDetails !== undefined) {
          this.userMobileNumber = userDetails.mobileNumber;
        } else {
          this.store.dispatch(logout.clearStore());
          this.router.navigateByUrl('auth/sign-up');
        }
      })
    );
    this.getUserDataSubscription.add(
      this.getSignUpResponse$.subscribe(vals => {
        if (vals !== undefined) {
          this.jwtToken = vals.accessToken;
        } else {
          this.store.dispatch(logout.clearStore());
          this.router.navigateByUrl('auth/sign-up');
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.getUserDataSubscription.unsubscribe();
  }

  firstTimeUserRegistration() {
    this.store.dispatch(register.clearRegisterFailure());
    this.store.dispatch(register.clearRegisterSuccess());
    const req: IRegistrationLoginRequest = {
      mobileNumber: this.userMobileNumber,
      passcode: this.passwordForm.value.passcode,
      make: 'web',
      model: 'web',
      totalDiskCapacity: '50',
      uniqueDeviceId: '00000000-0000-0000-0000-000000000000',
      firebaseTokenId: '00000000-0000-0000-0000-000000000000',
      dialingCode: '+27'
    };
    this.store.dispatch(register.loadRegisterRequest({ request: req }));
    this.isRegistrationLoading$ = this.store.select(registerSelector.getRegisterIsLoading);
    this.registrationFailure$ = this.store.select(registerSelector.getRegistrationFailure);
  }

  returningUserLogin() {
    this.store.dispatch(login.clearLoginFailure());
    this.store.dispatch(login.clearLoginSuccess());
    const req: IRegistrationLoginRequest = {
      mobileNumber: this.userMobileNumber,
      passcode: this.passwordForm.value.passcode,
      make: 'web',
      model: 'web',
      totalDiskCapacity: '50',
      uniqueDeviceId: '00000000-0000-0000-0000-000000000000',
      firebaseTokenId: '00000000-0000-0000-0000-000000000000',
      dialingCode: '+27'
    };
    this.store.dispatch(login.loadLoginRequest({ request: req }));
    this.isRegistrationLoading$ = this.store.select(loginSelector.getLoginIsLoading);
    this.registrationFailure$ = this.store.select(loginSelector.getLoginFailure);
  }

  signupSetPasscode() {
    const req: ISignupSetPasscode = {
      passcode: this.passwordForm.value.passcode,
      token: this.jwtToken,
      uniqueDeviceId: '00000000-0000-0000-0000-000000000000'
    };

    this.store.dispatch(fromSignupSetPasscodeActions.loadSignupSetPasscodes({ request: req }));
  }

  forgot() {
    this.store.dispatch(forgotPasscodeUserNeedsHelpActions.clearForgotPasswordsUserNeedsHelpFailure());
    const req: IForgotPasscodeRequest = {
      dialingCode: '+27',
      mobileNumber: this.userMobileNumber,
      uniqueDeviceId: '00000000-0000-0000-0000-000000000000',
      make: 'web',
      model: 'web',
      totalDiskCapacity: '50'
    };

    this.store.dispatch(forgotPasscodeUserNeedsHelpActions.loadForgotPasswordsUserNeedsHelpRequest({ request: req }));
    this.isForgotPasswordLoading$ = this.store.select(forgotPasscodeSelector.getForgotPasscodeUserNeedsHelpIsLoading);
    this.forgotPasswordFailure$ = this.store.select(forgotPasscodeSelector.getForgotPasscodeUserNeedsHelpFailure);
  }

  navBackBtn() {
    this.store.dispatch(logout.clearStore());
    this.router.navigateByUrl('auth/sign-up');
  }
}
